import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllCaseLogs, getAllUsers, getAppointments, getCaseDetailsLogs, getExportsLogs, getGlobalWorkQ, getReferralsApi, getTasks, getTransfersApi, getWorkQBySlugMaster } from "./fetchApi";
import { notificationActions } from "../store/notifications";
import { filterActions } from "../store/filter";
import { caseTypeOptions, retrieveSpecificWorkQ } from "./systemData";
import { referralsActions } from "../store/referrals";
import { caseActions } from "../store/case";
import { historyActions } from "../store/history";
import { userActions } from "../store/user";

import { workqueueActions } from "../store/workqueue";
import { tasksActions } from "../store/tasks";
import { traverseObject } from "./script";
import { exportsActions } from "../store/exports";
import { setBodyOnWorkQ } from "./workQ";
import { setFiltersBasedOnWorkQ } from "./utils";
import { resolveEffectiveSearchParams, shouldDeferWorkQListForSavedFilters } from "./filters";
import { viewTabPermission } from "./permissions";
import { tabs_button } from "../assets/data";
import { isManagingAttorney } from "./primaryItemScripts";
import { transfersActions } from "../store/transfers";
import { appointmentsActions } from "../store/appointments";
import store from "../store";
export async function fetchWorkQNow(workqSlug, overrides = {}) {
    const state = store.getState()
    const dispatch = store.dispatch

    const user = state.user.user
    const userToken = state.user.token
    const filters = overrides.filters ?? state.filter.filters
    const exceptions = overrides.exceptions ?? state.filter.exceptions
    const searchParams = overrides.searchParams ?? state.filter.searchParams
    const orderFilters = overrides.orderFilters ?? state.filter.orderFilters
    const onPage = overrides.onPage ?? state.filter.currentpage
    const initialLoadWorkq = state.workqueue.initialLoad

    if (!userToken || !user) {
        return null
    }

    const controller = new AbortController()
    const signal = controller.signal

    try {
        dispatch(filterActions.setMasterLoading(true))

        const workq = retrieveSpecificWorkQ(workqSlug)
        const effectiveSearchParams = resolveEffectiveSearchParams(searchParams, workqSlug)
        let body = setBodyOnWorkQ(
            workq,
            workqSlug,
            initialLoadWorkq,
            filters,
            exceptions,
            user,
            onPage,
            orderFilters,
            state.filter.savedViewsBySlug?.[workqSlug] || null,
            dispatch,
        )
        const incidentDateMegaFilter = body.incident_date
        const mrqEntryDateMegaFilter = body.mrq_entry_date
        const gdqExitDateMegaFilter = body.gdq_exit_date
        body = {
            ...body,
            ...(effectiveSearchParams || {}),
        }
        // Mega filter (GE/LE/range object or ISO date string) wins over free-text search on the same key.
        if (
            incidentDateMegaFilter
            && typeof body.incident_date === 'string'
            && (
                typeof incidentDateMegaFilter === 'object'
                || /^\d{4}-\d{2}-\d{2}(\|\d{4}-\d{2}-\d{2})?$/.test(String(incidentDateMegaFilter))
            )
        ) {
            body.incident_date = incidentDateMegaFilter
        }
        if (
            mrqEntryDateMegaFilter
            && typeof body.mrq_entry_date === 'string'
            && (
                typeof mrqEntryDateMegaFilter === 'object'
                || /^\d{4}-\d{2}-\d{2}(\|\d{4}-\d{2}-\d{2})?$/.test(String(mrqEntryDateMegaFilter))
            )
        ) {
            body.mrq_entry_date = mrqEntryDateMegaFilter
        }
        if (
            gdqExitDateMegaFilter
            && typeof body.gdq_exit_date === 'string'
            && (
                typeof gdqExitDateMegaFilter === 'object'
                || /^\d{4}-\d{2}-\d{2}(\|\d{4}-\d{2}-\d{2})?$/.test(String(gdqExitDateMegaFilter))
            )
        ) {
            body.gdq_exit_date = gdqExitDateMegaFilter
        }

        let patients
        if (workq && workq.type === 'custom') {
            patients = await getGlobalWorkQ(userToken, workq.apiSlug ? workq.apiSlug : workqSlug, body, signal)
        } else {
            patients = await getWorkQBySlugMaster(userToken, workqSlug, body, signal)
        }

        if (!patients) {
            // Aborted / null payload — don't overwrite existing state.
        } else if (patients.errorMessage) {
            dispatch(notificationActions.setError(patients))
        } else {
            dispatch(filterActions.setSnapshotData(patients))
            dispatch(filterActions.setCurrentPageData(patients))
            dispatch(filterActions.setWorkQslug(workq?.slug))
            dispatch(workqueueActions.setInitialLoad(workq?.slug))
        }

        return patients
    } catch (error) {
        console.error('[WorkQFetchNow] failed', error)
        dispatch(notificationActions.setError({
            errorMessage: 'Failed to load work queue. Please try again.',
            status: 500
        }))
        return null
    } finally {
        dispatch(filterActions.setMasterLoading(false))
        dispatch(filterActions.setRequesterId(null))
    }
}

export function startWorkQManualFetch(workqSlug, overrides = {}) {
    if (!workqSlug) {
        return null
    }

    const dispatch = store.dispatch
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()

    dispatch(filterActions.setMasterLoading(true))
    if (typeof window !== 'undefined') {
        window.__skipNextWorkQFetch = { slug: workqSlug, at: startedAt }
    }

    return fetchWorkQNow(workqSlug, { onPage: 1, ...overrides })
}

export function applyWorkQFilterChangeAndFetch(workqSlug, nextFilters) {
    if (!workqSlug) {
        return null
    }

    const dispatch = store.dispatch
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()

    const clonedFilters = JSON.parse(JSON.stringify(nextFilters))

    if (typeof window !== 'undefined') {
        window.__skipNextWorkQFetch = { slug: workqSlug, at: startedAt }
    }

    dispatch(filterActions.setFiltersMultiple(clonedFilters))
    dispatch(filterActions.setCurrentPage(1))
    dispatch(filterActions.setMasterLoading(true))

    return fetchWorkQNow(workqSlug, { filters: clonedFilters, onPage: 1 })
}

/**
 * Hook that alerts clicks outside of the passed ref
 * @param {boolean} [ignorePortalRoots] — when true, mousedown inside #overlay-root or #backdrop-root does not count as outside (portaled modals/tooltips)
 */
export function useOutsideAlerter(ref, task, ignorePortalRoots = false, extraInsideRefs) {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ignorePortalRoots) {
                const overlay = document.getElementById("overlay-root");
                const backdrop = document.getElementById("backdrop-root");
                const datepickerPortal = document.getElementById("datepicker-portal");
                if (
                    overlay?.contains(event.target) ||
                    backdrop?.contains(event.target) ||
                    datepickerPortal?.contains(event.target)
                ) {
                    return;
                }
            }
            const isInsideExtraRef = (extraInsideRefs || []).some(extraRef => extraRef?.current?.contains(event.target));
            if (ref.current && !ref.current.contains(event.target) && !isInsideExtraRef) {
                task();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, task, ignorePortalRoots, extraInsideRefs]);
}

export function useGetToken() {
    const token = useSelector(state => state.user.token)

    return token
}

function clearWorkQListLoading(dispatch) {
    dispatch(filterActions.setMasterLoading(false))
    dispatch(filterActions.setRequesterId(null))
}

/** True while fetchWorkQNow (sort, filters, per-page) owns the in-flight request. */
function isActiveManualWorkQFetch(workqSlug) {
    if (typeof window === 'undefined' || !workqSlug) return false
    const skip = window.__skipNextWorkQFetch
    if (!skip?.slug || skip.slug !== workqSlug || skip.at == null) return false
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    return (now - skip.at) < 10_000
}

export function useGetPatientsList(workqSlug, skip) {
    const dispatch = useDispatch()

    
    let filters = useSelector(state => state.filter.filters)
    let exceptions = useSelector(state => state.filter.exceptions)
    const searchParams = useSelector(state => state.filter.searchParams)
    const orderFilters = useSelector(state => state.filter.orderFilters)
    const onPage = useSelector(state => state.filter.currentpage)
    const userToken = useSelector(state => state.user.token)
    const refreshApi = useSelector(state => state.workqueue.refreshApi)
    const storeWorkQSlug = useSelector(state => state.filter.workqSlug)
    const cachedData = useSelector(state => state.filter.currentpagedata)
    const savedViewsBySlug = useSelector(state => state.filter.savedViewsBySlug)
    const savedViewsLoaded = useSelector(state => state.filter.savedViewsLoaded)

    const user = useSelector(state => state.user.user)

    const initialLoadWorkq = useSelector(state => state.workqueue.initialLoad)
    const settingsRevision = useSelector(state => state.system.settingsRevision)
    const latestRequestSeqRef = useRef(0)
    const prevEffectDepsRef = useRef({})
    const prevSkipRef = useRef(skip)
    const filtersRef = useRef(filters)
    const exceptionsRef = useRef(exceptions)
    filtersRef.current = filters
    exceptionsRef.current = exceptions

    useEffect(() => {
        const wasSkipped = prevSkipRef.current
        prevSkipRef.current = skip

        if (skip) {
            clearWorkQListLoading(dispatch)
            return
        }

        if (wasSkipped && workqSlug === storeWorkQSlug && cachedData != null) {
            clearWorkQListLoading(dispatch)
            return
        }

        // If a manual fetch was initiated (e.g. sort, filters, per-page), skip this effect
        // once to avoid double-fetching. Do not clear loading — fetchWorkQNow owns it.
        if (isActiveManualWorkQFetch(workqSlug)) {
            window.__skipNextWorkQFetch = null
            return
        }

        const settingsReinitPending =
            typeof window !== 'undefined' && window.__workQSettingsReinitSlug === workqSlug
        if (settingsReinitPending) {
            window.__workQSettingsReinitSlug = null
        }

        const prev = prevEffectDepsRef.current
        const settingsOnlyBump =
            prev.settingsRevision !== settingsRevision &&
            prev.workqSlug === workqSlug &&
            prev.orderFilters === orderFilters &&
            prev.onPage === onPage &&
            prev.refreshApi === refreshApi &&
            prev.user === user &&
            prev.searchParams === searchParams &&
            prev.skip === skip

        prevEffectDepsRef.current = {
            workqSlug,
            orderFilters,
            onPage,
            refreshApi,
            user,
            searchParams,
            skip,
            settingsRevision,
        }

        if (settingsOnlyBump && !settingsReinitPending && initialLoadWorkq === workqSlug) {
            return
        }

        // Police Report and Invalid Fax can save an empty filter set. Wait for that
        // view before the first request so preloaded clinics are not applied first.
        if (shouldDeferWorkQListForSavedFilters(workqSlug, savedViewsLoaded)) {
            return
        }

        const WORKQ_REQUEST_TIMEOUT_MS = 30_000
        const requestSeq = ++latestRequestSeqRef.current
        const controller = new AbortController()
        const signal = controller.signal     
        let timeoutId = null
        let didTimeoutAbort = false
        // user && 
        if (user) {
            if (!skip) {

                if (workqSlug != storeWorkQSlug) {
                    dispatch(filterActions.setSnapshotData(null))
                    dispatch(filterActions.setCurrentPageData(null))
                    // Don't wipe activeCase when transitioning from skipped state
                    // (drawer just opened on patient detail page) — we still have
                    // an active patient context that must stay populated.
                    if (!wasSkipped) {
                        dispatch(caseActions.setActiveCase(null))
                    }
                }
                let workq = retrieveSpecificWorkQ(workqSlug)

                const effectiveSearchParams = resolveEffectiveSearchParams(searchParams, workqSlug)
                let body = {}      
                body = setBodyOnWorkQ(
                    workq,
                    workqSlug,
                    initialLoadWorkq,
                    filtersRef.current,
                    exceptionsRef.current,
                    user,
                    onPage,
                    orderFilters,
                    savedViewsBySlug?.[workqSlug] || null,
                    dispatch,
                ) 
                body = {
                    ...body,
                    ...(effectiveSearchParams || {})
                }
                
            
                const getPatientLists = async (noLoading) => {
                    if (userToken) {
                        dispatch(filterActions.setMasterLoading(true))
                        timeoutId = setTimeout(() => {
                            didTimeoutAbort = true
                            controller.abort()
                        }, WORKQ_REQUEST_TIMEOUT_MS)
                        try {
                            // const patients = await getWorkQBySlugMaster(userToken, workqSlug, onPage, filtersString, orderString)
                            let patients

                            if (workq && workq.type == 'custom') {
                                patients = await getGlobalWorkQ(userToken, workq.apiSlug ? workq.apiSlug : workqSlug, body, signal)
                            } else {
                                patients = await getWorkQBySlugMaster(userToken, workqSlug, body, signal)
                            }

                            // Ignore stale responses; latest request wins.
                            if (requestSeq !== latestRequestSeqRef.current) {
                                return
                            }

                            // `genericFetchBearer` returns null on AbortError (e.g. timeout
                            // abort, navigation race). Don't treat that as a successful
                            // empty payload — it would clobber existing data and the slug.
                            if (!patients) {
                                if (didTimeoutAbort) {
                                    dispatch(notificationActions.setError({
                                        errorMessage: 'Work queue request timed out. Please retry.',
                                        status: 408,
                                    }))
                                }
                            } else if (patients.errorMessage) {
                                dispatch(notificationActions.setError(patients))
                            } else {
                                dispatch(filterActions.setSnapshotData(patients)) // this is a snapshot to show because a loading page is bad! :))
                                dispatch(filterActions.setCurrentPageData(patients))
                                dispatch(filterActions.setWorkQslug(workq?.slug))
                                dispatch(workqueueActions.setInitialLoad(workq?.slug))
                            }
                        } catch (error) {
                            if (signal.aborted || error?.name === 'AbortError') {
                                if (didTimeoutAbort && requestSeq === latestRequestSeqRef.current) {
                                    dispatch(notificationActions.setError({
                                        errorMessage: 'Work queue request timed out. Please retry.',
                                        status: 408
                                    }))
                                }
                                return
                            }
                            console.error('Failed to load work queue', error)
                            if (requestSeq === latestRequestSeqRef.current) {
                                dispatch(notificationActions.setError({
                                    errorMessage: 'Failed to load work queue. Please try again.',
                                    status: 500
                                }))
                            }
                        } finally {
                            if (timeoutId) {
                                clearTimeout(timeoutId)
                                timeoutId = null
                            }
                            // Only the latest request should control the global loading flag.
                            if (requestSeq === latestRequestSeqRef.current) {
                                clearWorkQListLoading(dispatch)
                            }
                        }
                    }
                }
                getPatientLists()
            }
        }
        
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
                timeoutId = null
            }
            const abortedSeq = requestSeq
            controller.abort()
            // Abort races (navigate to patient mid-fetch) can strand loading if finally
            // does not run for the latest seq; clear when this effect owned the request.
            if (abortedSeq === latestRequestSeqRef.current && !isActiveManualWorkQFetch(workqSlug)) {
                clearWorkQListLoading(dispatch)
            }
        };
        
    }, [workqSlug, orderFilters, onPage, refreshApi, user, searchParams, skip, settingsRevision, savedViewsLoaded]); // filters/exceptions read via refs so setBodyOnWorkQ default-filter dispatches do not retrigger and abort this effect


    return null
}

export function useGetFiltersForWorkQs() {
    let filters = useSelector(state => state.filter.filters)
    let exceptions = useSelector(state => state.filter.exceptions)
    const orderFilters = useSelector(state => state.filter.orderFilters)
    const onPage = useSelector(state => state.filter.currentpage)
    const user = useSelector(state => state.user.user)
    const initialLoadWorkq = useSelector(state => state.workqueue.initialLoad)

    return {filters, exceptions, orderFilters, onPage, user, initialLoadWorkq}
}

export function useGetReferralsList() {
    const dispatch = useDispatch()
    const filters = useSelector(state => state.referrals.filters)
    const orderFilters = useSelector(state => state.referrals.orderFilters)
    const onPage = useSelector(state => state.referrals.currentpage)
    const userToken = useSelector(state => state.user.token)

    useEffect(() => {     
        let isMounted = true;
        let filtersString = setFiltersBasedOnWorkQ(filters)     

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null


        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(referralsActions.setMasterLoading(true))
                const referrals = await getReferralsApi(userToken, filtersString, orderString, onPage)
                
                if (referrals && referrals.errorMessage) {
                    dispatch(notificationActions.setError(referrals))
                } else {
                    dispatch(referralsActions.setSnapshotData(referrals)) // this is a snapshot to show because a loading page is bad! :))
                    dispatch(referralsActions.setCurrentPageData(referrals))    
                }
                dispatch(referralsActions.setMasterLoading(false))
                dispatch(referralsActions.setRequesterId(null))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [orderFilters, filters, onPage]);


    return null
}

export function useGetTransfersList() {
    const dispatch = useDispatch()
    const filters = useSelector(state => state.transfers.filters)
    const orderFilters = useSelector(state => state.transfers.orderFilters)
    const onPage = useSelector(state => state.transfers.currentpage)
    const userToken = useSelector(state => state.user.token)
    const refreshApi = useSelector(state => state.transfers.refreshApi)

    useEffect(() => {     
        let isMounted = true;
        let filtersString = setFiltersBasedOnWorkQ(filters)     

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null

        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(transfersActions.setMasterLoading(true))
                const transfers = await getTransfersApi(userToken, filtersString, orderString, onPage)
                
                if (transfers && transfers.errorMessage) {
                    dispatch(notificationActions.setError(transfers))
                } else {
                    dispatch(transfersActions.setSnapshotData(transfers)) // this is a snapshot to show because a loading page is bad! :))
                    dispatch(transfersActions.setCurrentPageData(transfers))    
                }
                dispatch(transfersActions.setMasterLoading(false))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [orderFilters, filters, onPage, refreshApi]);


    return null
}

export function useGetTransfersListDashboard(perPage) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const refreshApi = useSelector(state => state.transfers.refreshApi)

    useEffect(() => {     
        let isMounted = true;
        let filtersString = `&per_page=${perPage ? perPage : 5}&transfer_in=1&&statuses=Review,Pending`    

        let orderString = null

        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(transfersActions.setMasterLoading(true))
                const transfers = await getTransfersApi(userToken, filtersString, orderString, 1)
                
                if (transfers && transfers.errorMessage) {
                    dispatch(notificationActions.setError(transfers))
                } else {
                    dispatch(transfersActions.setDashboardList(transfers)) 
                }
                dispatch(transfersActions.setMasterLoading(false))
                dispatch(transfersActions.setRequesterId(null))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [refreshApi]);


    return null
}

export function useHistoryList(caseId, caseIdFromDetails) {
    const dispatch = useDispatch()
    const filters = useSelector(state => state.history.filters)
    const orderFilters = useSelector(state => state.history.orderFilters)
    const onPage = useSelector(state => state.history.currentpage)
    const userToken = useSelector(state => state.user.token)
    const allUsersFromStore = useSelector(state => state.user.allUsers)

    // Do not list `allUsersFromStore` in deps: this effect dispatches `setAllUsers`, which would retrigger in a loop.
    useEffect(() => {
        let isMounted = true
        let filtersString = setFiltersBasedOnWorkQ(filters)

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null

        const fetchData = async (noLoading) => {
            if (!isMounted) return
            if (userToken) {
                dispatch(historyActions.setMasterLoading(true))

                let patients
                if (caseId) {
                    patients = await getCaseDetailsLogs(userToken, caseId, filtersString, orderString, onPage)
                } else {
                    patients = await getAllCaseLogs(userToken, filtersString, orderString, onPage)
                }

                if (patients && patients.errorMessage) {
                    dispatch(notificationActions.setError(patients))
                } else {
                    dispatch(historyActions.setSnapshotData(patients))
                    dispatch(historyActions.setCurrentPageData(patients))

                    let allUsers
                    if (!allUsersFromStore) {
                        allUsers = await getAllUsers(userToken)
                    } else {
                        allUsers = allUsersFromStore
                    }

                    if (allUsers && allUsers.errorMessage) {
                        dispatch(notificationActions.setError(patients))
                    } else {
                        let users = allUsers.map((item) => {
                            return {
                                ...item,
                                value: item.id,
                                label: item.name,
                            }
                        })
                        dispatch(userActions.setAllUsers(users))
                    }
                }
                dispatch(historyActions.setMasterLoading(false))
                dispatch(historyActions.setRequesterId(null))
            }
        }
        fetchData()

        return () => {
            isMounted = false
        }
    }, [orderFilters, filters, onPage, caseId, userToken])

    return null
}

export function useAppointmentsList(caseId, options = {}) {
    const enabled = options.enabled !== false
    const dispatch = useDispatch()
    const filters = useSelector(state => state.appointments.filters)
    const orderFilters = useSelector(state => state.appointments.orderFilters)
    const onPage = useSelector(state => state.appointments.currentpage)
    const userToken = useSelector(state => state.user.token)
    const allUsersFromStore = useSelector(state => state.user.allUsers)

    // Do not list `allUsersFromStore` in deps: this effect dispatches `setAllUsers`, which would retrigger in a loop.
    useEffect(() => {
        if (!enabled) {
            return undefined
        }

        let isMounted = true
        let filtersString = setFiltersBasedOnWorkQ(filters)

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null

        const fetchData = async (noLoading) => {
            if (!isMounted) return
            if (userToken) {
                dispatch(appointmentsActions.setMasterLoading(true))

                let patients

                if (caseId) {
                    patients = await getAppointments(userToken, caseId, filtersString, orderString, onPage)
                } else {
                    patients = await getAppointments(userToken, filtersString, orderString, onPage)
                }
                if (patients && patients.errorMessage) {
                    dispatch(notificationActions.setError(patients))
                } else {
                    dispatch(appointmentsActions.setSnapshotData(patients))
                    dispatch(appointmentsActions.setCurrentPageData(patients))

                    let allUsers
                    if (!allUsersFromStore) {
                        allUsers = await getAllUsers(userToken)
                    } else {
                        allUsers = allUsersFromStore
                    }

                    if (allUsers && allUsers.errorMessage) {
                        dispatch(notificationActions.setError(patients))
                    } else {
                        let users = allUsers.map((item) => {
                            return {
                                ...item,
                                value: item.id,
                                label: item.name,
                            }
                        })
                        dispatch(userActions.setAllUsers(users))
                    }
                }
                dispatch(appointmentsActions.setMasterLoading(false))
                dispatch(appointmentsActions.setRequesterId(null))
            }
        }
        fetchData()

        return () => {
            isMounted = false
        }
    }, [orderFilters, filters, onPage, caseId, enabled, userToken])

    return null
}

export function useTasksList(caseId, caseIdFromDetails) {
    const dispatch = useDispatch()
    const filters = useSelector(state => state.tasks.filters)
    const orderFilters = useSelector(state => state.tasks.orderFilters)
    const onPage = useSelector(state => state.tasks.currentpage)
    const userToken = useSelector(state => state.user.token)
    const refreshApi = useSelector(state => state.tasks.refreshApi)

    
    useEffect(() => {     
        let isMounted = true;
        let filtersString = setFiltersBasedOnWorkQ(filters)     

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null

        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(tasksActions.setMasterLoading(true)) 

                let tasks = await getTasks(userToken, filtersString, orderString, onPage)

                if (tasks && tasks.errorMessage) {
                    dispatch(notificationActions.setError(tasks))
                    
                } else {
                    dispatch(tasksActions.setSnapshotData(tasks)) // this is a snapshot to show because a loading page is bad! :))
                    dispatch(tasksActions.setCurrentPageData(tasks))    
                    
                }
                dispatch(tasksActions.setMasterLoading(false))
                dispatch(tasksActions.setRequesterId(null))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [orderFilters, filters, onPage, caseId, refreshApi]);


    return null
}

export function useGetTasksListDashboard(perPage) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const refreshApi = useSelector(state => state.tasks.refreshApi)

    useEffect(() => {     
        let isMounted = true;
        let filtersString = `&per_page=${perPage ? perPage : 5}`    

        let orderString = null

        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(tasksActions.setMasterLoading(true))
                const tasks = await getTasks(userToken, filtersString, orderString, 1)
                
                if (tasks && tasks.errorMessage) {
                    dispatch(notificationActions.setError(tasks))
                } else {
                    dispatch(tasksActions.setDashboardList(tasks)) 
                }
                dispatch(tasksActions.setMasterLoading(false))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [refreshApi]);


    return null
}

export function useExportsList() {
    const dispatch = useDispatch()
    const filters = useSelector(state => state.exports.filters)
    const orderFilters = useSelector(state => state.exports.orderFilters)
    const onPage = useSelector(state => state.exports.currentpage)
    const userToken = useSelector(state => state.user.token)
    // const allUsersFromStore = useSelector(state => state.user.allUsers)

    useEffect(() => {     
        let isMounted = true;
        let filtersString = setFiltersBasedOnWorkQ(filters)     

        let orderString = orderFilters ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}` : null

        const fetchData = async (noLoading) => {
            if (!isMounted) return;
            if (userToken) {
                dispatch(exportsActions.setMasterLoading(true))

                const exports = await getExportsLogs(userToken, filtersString, orderString, onPage)

                if (exports && exports.errorMessage) {
                    dispatch(notificationActions.setError(exports))
                    
                } else {
                    dispatch(exportsActions.setSnapshotData(exports)) // this is a snapshot to show because a loading page is bad! :))
                    dispatch(exportsActions.setCurrentPageData(exports))    

                }
                dispatch(exportsActions.setMasterLoading(false))
                dispatch(exportsActions.setRequesterId(null))
            }
        }
        fetchData()
        
        return () => {
            isMounted = false;
        };
    }, [orderFilters, filters, onPage]);


    return null
}

export function useGetActiveTab() {
    const activeCase = useSelector(state => state.case.activeCase) 
    const user = useSelector(state => state.user.user)
    let permission_tabs = viewTabPermission(tabs_button, user?.permissions)

    return useMemo(() => {
        if (!activeCase?.case?.case_type_id) return permission_tabs;
        
        const foundItem = caseTypeOptions()?.find(item => 
            item.value === activeCase.case.case_type_id
        );
        
        if (!foundItem) return permission_tabs;


        let difference     

        const toRemove = new Set(foundItem.tabs);
        difference = permission_tabs.filter( x => toRemove.has(x.tab) );

        if(!isManagingAttorney(activeCase)) {// there isn't  a managing attorney, remove the procodocs
            let arr = [19]
            const toRemove = new Set(arr);
            difference = difference.filter( x => !toRemove.has(x.id) );
        }   

        let remaining = permission_tabs.filter( x => !toRemove.has(x.tab) );
        remaining.forEach(item => { // search in remaining tabs the ones that have data in

            if (item.id != 26 && item.id != 36 && item.id != 15) { // leave flags and alerts out of this
                
                if (item.folder == 'lien') {
                    // logic for lien 
                    if (activeCase.practices && activeCase.practices.some(practice => practice.liens && practice.liens.length > 0)) {
                        difference.push(item)
                    }
                } else {
                    if (activeCase[item.folder]) {
                        if (activeCase[item.folder].constructor == Array) { // if array, check for length
                            if (activeCase[item.folder].length) {
                                difference.push(item)
                            }
                        } else {
                            let ignoreFields
                            let specificFields
                            switch (item.folder) {
                                case 'case_negotiation':
                                    ignoreFields = ['case_id', 'entity', 'entity_type', 'entity_type_id']
                                    break;
                                case 'crime_victim':
                                    specificFields = ['filled_by_patient']
                                    break;
                                default:
                                    break;
                            }
                        
                            if (traverseObject(activeCase[item.folder], ignoreFields, specificFields)) { // if object, search for a truthy value
                                difference.push(item)
                            }
                        }
                    }
                }
                
            }
        })

        //need to sort the tabs to show them in the same order -> based on "order" property
        let differenceOrdered = difference.sort(function(x, y) {
            return new Date(x.order) - new Date(y.order)
        });
        
        // ... filtering logic
        return differenceOrdered;
    }, [activeCase, permission_tabs]);
}